'use client';

import HomePageTitle from '@/app/(home-page)/components/HomePageTitle';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import type { JumpTargetItem, NavigationShapeProps, TrackingParams } from './types';
import { IS_DE_FR } from '@/utils/constants';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';

/**
 * getAlt工具函数
 */
const getAlt = (componentName: string, index: number, indexData?: any): string => {
  try {
    if (!indexData?.jump_target) return `${componentName} ${index}`;

    const item = indexData.jump_target[index];
    if (!item) return `${componentName} ${index}`;

    // 优先级: title > 商品名称 > 分组名称 > 默认
    return (
      item.title || item.sku?.short_name || item.product_group?.name || `${componentName} ${index}`
    );
  } catch (error) {
    logger.error('getAlt error:', error);
    return `${componentName} ${index}`;
  }
};

/**
 * 埋点参数构建Hook
 */
const useTrackingParams = (
  item: JumpTargetItem,
  index: number,
  moduleIndex: number,
  componentId: string,
  moduleTitle: string,
  moduleId: string | number,
): TrackingParams => {
  return useMemo(
    () => ({
      module_index: moduleIndex,
      module_name: componentId,
      module_id: String(moduleId),
      mkt_index: index,
      mkt_index_sub: '',
      mkt_jump_type: `${item.jump_type}`,
      mkt_jump_content: `${item.Jump_target}`,
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: `${item.sku?.shop_sku || ''}`,
      business_sku: `${item.sku?.business_sku || ''}`,
      product_sku: `${item.sku?.sku || ''}`,
      product_id: `${item.sku?.id || ''}`,
      product_name: `${item.sku?.short_name || ''}`,
      group_id: `${item.product_group?.id || ''}`,
      group_name: `${item.product_group?.name || ''}`,
      module_title: moduleTitle,
    }),
    [item, index, moduleIndex, componentId, moduleTitle, moduleId],
  );
};

/**
 * 埋点逻辑Hook
 */
const useTrackingHandlers = (
  trackingParams: TrackingParams,
  onTrack?: (params: TrackingParams) => void,
  onExpTrack?: (params: TrackingParams) => void,
  onValidExpTrack?: (params: TrackingParams) => void,
) => {
  // 曝光埋点
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 处理曝光埋点
  useEffect(() => {
    if (inView && onExpTrack) {
      try {
        onExpTrack(trackingParams);
      } catch (error) {
        logger.error('Navigation exposure tracking error:', error);
      }
    }
  }, [inView, onExpTrack, trackingParams]);

  // 处理点击埋点
  const handleClick = useCallback(() => {
    if (onTrack) {
      try {
        onTrack(trackingParams);
      } catch (error) {
        logger.error('Navigation click tracking error:', error);
      }
    }
  }, [onTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  return { expRef, handleClick, validExposureRef };
};

/**
 * 导航项组件 - 响应式统一实现
 */
const NavigationItem = ({
  item,
  index,
  onTrack,
  onExpTrack,
  moduleIndex,
  componentId,
  moduleTitle,
  indexData,
  onValidExpTrack,
}: {
  item: JumpTargetItem;
  index: number;
  onTrack?: (params: TrackingParams) => void;
  onExpTrack?: (params: TrackingParams) => void;
  onValidExpTrack?: (params: TrackingParams) => void;
  moduleIndex: number;
  componentId: string;
  moduleTitle: string;
  indexData: any;
}) => {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 获取埋点参数
  const trackingParams = useTrackingParams(
    item,
    index,
    moduleIndex,
    componentId,
    moduleTitle,
    indexData?.blocks_advances_id,
  );

  // 获取埋点处理函数
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );
  // MagicJump props
  const jumpProps = useMemo(
    () => ({
      jump_type: item.jump_type,
      Jump_target: item.Jump_target,
      ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
      ...(item.product_group?.id && {
        product_group: { id: item.product_group.id, path: item.product_group.path },
      }),
    }),
    [item],
  );

  const navigationItemRef = useMergedRefs(expRef, validExposureRef);
  return (
    <div
      ref={navigationItemRef}
      className={`
        ${isPC ? '' : 'rounded-[38px]'}
      `}
      style={!isPC ? { backgroundColor: item.text_bg_color || '#ffffff' } : {}}
    >
      <MagicJump
        prefetch
        item={jumpProps}
        onBeforeJump={handleClick}
        aria-label={getAlt('CategoryNavShape', index, indexData)}
        className={`
          ${jumpProps?.Jump_target ? 'cursor-pointer' : ''}
          ${
            isPC
              ? 'flex flex-col items-center justify-center'
              : 'flex items-center justify-center w-full h-[45px] gap-[3px]'
          }
          ${IS_DE_FR ? 'flex-col' : ''}
        `}
      >
        {isPC ? (
          // PC端：垂直布局 - 图片在上，文字在下
          <>
            <Image
              src={item.jump_image}
              alt={getAlt('CategoryNavShape', index, indexData)}
              width={116}
              height={116}
              loading="eager"
              className="w-[70px] h-auto lg:w-[90px] 2xl:w-[110px] object-cover"
              style={{
                aspectRatio: item.jump_img_ratio || 'auto',
              }}
            />
            <span
              className="mt-[20px] lg:mt-[24px] 2xl:mt-[28px] text-[12px] lg:text-[15px] 2xl:text-[16px] text-center"
              style={{ color: item.text_color || '#282828' }}
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
          </>
        ) : (
          // 移动端：水平布局 - 图片在左，文字在右，整体居中
          <>
            <Image
              src={item.jump_image}
              alt={getAlt('CategoryNavShape', index, indexData)}
              width={33}
              height={33}
              loading="eager"
              className="w-[33px] h-auto object-cover flex-shrink-0"
              style={{
                aspectRatio: item.jump_img_ratio || 'auto',
              }}
            />
            <span
              className="text-[12px] leading-[12px] text-center"
              style={{ color: item.text_color || '#282828' }}
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
          </>
        )}
      </MagicJump>
    </div>
  );
};

/**
 * 形状分类导航组件 - 基于原Vue组件的样式迁移
 */
export default function CategoryNavShape({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: NavigationShapeProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 容器背景色
  const containerStyle = useMemo(() => {
    return {
      backgroundColor: indexData.bg_color || (isPC ? '#EEEEEE' : undefined),
    };
  }, [indexData.bg_color, isPC]);

  // 如果没有数据，不渲染
  if (!indexData?.jump_target?.length) {
    return null;
  }

  return (
    <div className={`${isPC ? 'mx-[24px] lg:mx-[48px]' : 'w-full'}`} style={containerStyle}>
      <div
        className={`
          ${
            isPC
              ? 'my-[30px] pt-[40px] pb-[30px] lg:my-[50px] lg:pt-[50px] lg:pb-[40px] 2xl:my-[80px] 2xl:pt-[70px] 2xl:pb-[60px]'
              : 'my-[40px] py-[35px]'
          }
        `}
      >
        {/* 标题部分 - PC和移动端都显示 */}
        <div className={`${isPC ? 'mb-[46px]' : 'mb-[24px]'}`}>
          <HomePageTitle
            indexData={indexData}
            variant="default"
            displayCondition="all"
            titleTag={isPC ? 'h3' : 'h2'}
            subtitleTag={isPC ? 'h4' : 'h3'}
            isNew
            hasButton={false}
          />
        </div>

        {/* 导航项列表容器 */}
        <div
          className={`
            ${
              isPC
                ? 'flex flex-wrap items-center justify-center mt-[20px] lg:mt-[30px] 2xl:mt-[46px] gap-y-[16px] gap-x-[20px] lg:gap-y-[20px] lg:gap-x-[30px] 2xl:gap-y-[24px] 2xl:gap-x-[40px] px-[4.1666666667vw]'
                : 'px-[20px] grid grid-cols-3 gap-[5px] mt-[24px]'
            }
          `}
        >
          {indexData.jump_target.map((item, index) => (
            <NavigationItem
              key={item.id || index}
              item={item}
              index={index}
              onTrack={onTrack}
              onExpTrack={onExpTrack}
              moduleIndex={moduleIndex}
              componentId={indexData.component_id}
              moduleTitle={indexData.layout_title}
              indexData={indexData}
              onValidExpTrack={onValidExpTrack}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
